/**
 * SAT Report - the shared report renderer.
 *
 * The skin report is shown on two pages that reach it differently: the
 * results page (sections/sat-results.liquid) renders it from sessionStorage
 * right after an assessment, and the assessment detail page
 * (sections/my-assessments.liquid) renders it from a fetched historical
 * assessment. They used to carry two hand-maintained copies of this markup
 * and pricing logic, which drifted - the card and the cart-update message
 * ended up quoting different prices for the same product.
 *
 * Everything here is presentation and cart plumbing that both pages must
 * agree on. Page-specific chrome stays in the sections: the results page owns
 * the chat log and its cart-update bubble, the detail page owns the theme's
 * cart notification and the GoKwik side cart.
 */
(function (global) {
  "use strict";

  var CHECK_SVG =
    '<svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';

  // Marks every line the SAT flow adds. The underscore keeps it out of the
  // customer-facing cart and checkout while leaving it on the order line item.
  var SAT_LINE_PROPERTY = "_sat_added_item";

  // The cart-level counterpart, which the order carries as a note attribute:
  // the line property attributes the item, this attributes the order.
  var SAT_CART_ATTRIBUTE = "via_sat";

  // How long an Add to cart button holds its 'Added' confirmation before it
  // reads as 'Add to cart' again.
  var ADDED_REVERT_MS = 1500;

  function esc(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function money(paise) {
    return "₹" + ((Number(paise) || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  }

  // ---------------------------------------------------------------- pricing

  function firstAvailableVariant(shopify) {
    var variants = (shopify && shopify.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return variants[0];
  }

  /**
   * The one price every surface quotes: the variant Add to cart actually
   * sends. A product's own `price` is the product-level figure and can belong
   * to a different variant than firstAvailableVariant picks, which is how a
   * card and the cart-update message came to disagree.
   */
  function variantPrice(shopify) {
    var v = firstAvailableVariant(shopify) || {};
    return Number(v.price != null ? v.price : shopify && shopify.price) || 0;
  }

  /**
   * What the card quotes: { price, sale, compare, pct }.
   *
   * On this store the Shopify price is the MRP, not the selling price - the
   * selling price is derived from the custom.discount_percentage metafield
   * (snippets/card-price.liquid). The metafield is not in /products/x.js, so
   * the figures come from the ?view=sat-card template, which computes them
   * with the theme's own rules. Nothing is recomputed here: a second
   * implementation of that rounding is how the card and the PDP would drift.
   *
   * Payloads without those fields - the .js fallback - simply quote the
   * variant price with nothing struck through, which is how cards read
   * before the metafield was available.
   */
  function pricingOf(shopify) {
    var price = variantPrice(shopify);
    var s = shopify || {};
    var sale = Number(s.sale_price != null ? s.sale_price : price) || 0;
    var compare = Number(s.compare_at_price) || 0;
    var pct = Number(s.discount_pct) || 0;

    // Only ever slash a genuine saving, whatever the payload claims.
    if (!(compare > sale)) return { price: price, sale: sale, compare: 0, pct: 0 };
    return { price: price, sale: sale, compare: compare, pct: pct };
  }

  /**
   * The price to quote for one added line. `original_line_price` - the variant
   * price before any automatic discount - is preferred over `final_line_price`
   * because automatic discounts still apply and show up in the cart and at
   * checkout.
   *
   * This is the MRP the card strikes through, not the price it quotes - the
   * cart AJAX API carries no metafields. Callers showing a customer-facing
   * figure want addedLinePricing, which pairs this with the card's own
   * selling price; this function on its own is the pre-discount side of it.
   */
  function addedLineTotal(response) {
    var line = (response && (response.items ? response.items[0] : response)) || {};
    if (line.original_line_price != null) return line.original_line_price;
    if (line.line_price != null) return line.line_price;
    return line.price != null ? line.price : 0;
  }

  /**
   * What the cart-update bubble should quote for one added line, as
   * { total, saving, pct } - the struck MRP, the saving off it, and the rate.
   *
   * Mirrors the guard in snippets/line-item-price.liquid:93. The metafield
   * selling price is a preview of a discount Shopify has not applied to the
   * line yet, so it must step aside the moment a real one lands - otherwise a
   * discounted line reads as discounted twice, once by the metafield and
   * again by Shopify.
   *
   * `card` is the pricing the clicked card quoted (see pricingFromCard), so
   * the bubble repeats the figures the customer just clicked rather than
   * deriving its own. `addedQty` is what this click added - not the
   * quantity the line ended up holding.
   */
  function addedLinePricing(response, card, addedQty) {
    var line = (response && (response.items ? response.items[0] : response)) || {};
    var qty = Math.max(1, Number(addedQty) || 1);

    // Everything below is per unit, then scaled by what this click added.
    // /cart/add.js answers with the whole resulting line, so adding one more
    // of something already in the cart comes back as a line of two - quoting
    // the line would bill the customer's earlier click to this message.
    var lineQty = Math.max(1, Number(line.quantity) || 1);
    var unitWas =
      line.original_price != null
        ? Number(line.original_price)
        : Math.round(addedLineTotal(response) / lineQty);
    var unitNow = line.final_price != null ? Number(line.final_price) : unitWas;

    var allocations = line.line_level_discount_allocations || [];

    // A real Shopify discount is on the line - quote it, not the preview.
    if (allocations.length > 0 || unitNow !== unitWas) {
      var total = unitWas * qty;
      var real = Math.max(0, unitWas - unitNow) * qty;
      return { total: total, saving: real, pct: total > 0 ? Math.round((real / total) * 100) : 0 };
    }

    var c = card || {};
    var compare = (Number(c.compare) || 0) * qty;
    var sale = (Number(c.sale) || 0) * qty;

    if (compare > sale && sale > 0) {
      return { total: compare, saving: compare - sale, pct: Number(c.pct) || 0 };
    }
    return { total: unitWas * qty, saving: 0, pct: 0 };
  }

  /** Reads back the pricing productCardHTML rendered onto a card. */
  function pricingFromCard(el) {
    if (!el) return null;
    return {
      sale: Number(el.dataset.satrProductSale) || 0,
      compare: Number(el.dataset.satrProductCompare) || 0,
      pct: Number(el.dataset.satrProductOff) || 0,
    };
  }

  // --------------------------------------------------------------- severity

  /**
   * Severity and colour both come from the score so they can never disagree.
   * The API's own severity string is deliberately ignored.
   *
   * `opts.colours` maps each band to whatever the calling page paints with -
   * a CSS variable reference on the results page, a hex literal on the
   * detail page.
   */
  function severityOf(score, opts) {
    var o = opts || {};
    var colours = o.colours || {};
    var severeBelow = Number(o.severeBelow != null ? o.severeBelow : 45);
    var mildAbove = Number(o.mildAbove != null ? o.mildAbove : 70);

    if (score < severeBelow) return { label: "Severe", colour: colours.severe || "#D22326" };
    if (score <= mildAbove) return { label: "Moderate", colour: colours.moderate || "#FE8C26" };
    return { label: "Mild", colour: colours.mild || "#11AF91" };
  }

  // --------------------------------------------------------------- concerns

  /**
   * `opts.copy` is the per-concern description keyed by concern name; a
   * concern with no copy simply renders without a description line.
   */
  function concernsHTML(concerns, opts) {
    var o = opts || {};
    var copy = o.copy || {};

    return (concerns || [])
      .map(function (c) {
        var score = Number(c.score) || 0;
        var sev = severityOf(score, o);
        var colour = sev.colour;
        // Bar reads as the score itself - 85/100 fills 85%. Severity stays in
        // the colour, so a high score is a long bar in the mild green.
        var fill = Math.max(0, Math.min(100, score));

        // Two equal halves: the concern on the left, its reading on the right.
        return (
          '<div class="satr-concern">' +
            '<div class="satr-concern__body">' +
              '<p class="satr-concern__name">' + esc(c.name) + "</p>" +
              (copy[c.name] ? '<p class="satr-concern__desc">' + esc(copy[c.name]) + "</p>" : "") +
            "</div>" +
            '<div class="satr-concern__meta">' +
              '<div class="satr-concern__stats">' +
                '<span class="satr-concern__score" style="color:' + colour + '">' + score + "<span>/100</span></span>" +
                '<span class="satr-concern__sev" style="color:' + colour + '">' + sev.label + "</span>" +
              "</div>" +
              '<div class="satr-concern__track" style="background:#f0f0f0;background:color-mix(in srgb, ' + colour + ' 20%, #fff)">' +
                '<span class="satr-concern__bar" style="background:' + colour + ";width:" + fill + '%"></span>' +
              "</div>" +
            "</div>" +
          "</div>"
        );
      })
      .join("");
  }

  // --------------------------------------------------------------- products

  /**
   * One product, priced the way the rest of the theme prices it.
   *
   * ?view=sat-card is an alternate product template returning JSON, used in
   * place of /products/{handle}.js because only Liquid can see the
   * custom.discount_percentage metafield the selling price is derived from.
   * Its payload is a superset of the .js one, so the .js endpoint stays a
   * working fallback: a theme deploy that has not picked the template up yet
   * degrades to unslashed cards rather than an empty report.
   */
  // A product request that has not answered by now is treated as failed, so
  // one stalled request cannot hold back every card on the report.
  var PRODUCT_TIMEOUT_MS = 8000;

  function fetchWithTimeout(url) {
    var controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, PRODUCT_TIMEOUT_MS)
      : null;
    return fetch(url, controller ? { signal: controller.signal } : undefined).then(
      function (res) {
        clearTimeout(timer);
        return res;
      },
      function (e) {
        clearTimeout(timer);
        throw e;
      }
    );
  }

  // A 404 means the handle is wrong and asking again will not change that.
  // Anything else - a timeout, a dropped connection, a 429 or a 5xx - is
  // worth one more try.
  function httpError(res) {
    var err = new Error("HTTP " + res.status);
    err.status = res.status;
    return err;
  }

  function isNotFound(e) {
    return e && e.status === 404;
  }

  function fetchProductOnce(handle, label) {
    var slug = encodeURIComponent(handle);

    return fetchWithTimeout("/products/" + slug + "?view=sat-card")
      .then(function (res) {
        if (!res.ok) throw httpError(res);
        // The template renders as a page, so the body is JSON with an HTML
        // content type - parse it rather than trusting res.json().
        return res.text();
      })
      .then(function (body) {
        var data = JSON.parse(body);
        if (!data || !data.handle || !Array.isArray(data.variants)) {
          throw new Error("sat-card view returned an unexpected payload");
        }
        return data;
      })
      .catch(function (e) {
        // The .js endpoint 404s for the same handles the view does.
        if (isNotFound(e)) throw e;
        console.warn(label + " sat-card view unavailable, falling back", handle, e);
        return fetchWithTimeout("/products/" + slug + ".js").then(function (res) {
          if (!res.ok) throw httpError(res);
          return res.json();
        });
      });
  }

  /**
   * One product, priced the way the rest of the theme prices it.
   *
   * ?view=sat-card is an alternate product template returning JSON, used in
   * place of /products/{handle}.js because only Liquid can see the
   * custom.discount_percentage metafield the selling price is derived from.
   * Its payload is a superset of the .js one, so the .js endpoint stays a
   * working fallback: a theme deploy that has not picked the template up yet
   * degrades to unslashed cards rather than an empty report.
   */
  function fetchProduct(handle, label) {
    return fetchProductOnce(handle, label).catch(function (e) {
      if (isNotFound(e)) throw e;
      console.warn(label + " Retrying product", handle, e);
      return fetchProductOnce(handle, label);
    });
  }

  // Shopify's own rule for turning a title into a handle.
  function toHandle(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  /**
   * Every handle worth trying for one report product, best guess first. The
   * report normally puts the handle in `name`, but the field is not
   * guaranteed: it can carry the product title instead, with the handle in
   * `handle`, or with different casing or stray whitespace.
   */
  function handleCandidates(p) {
    var out = [];
    function add(value) {
      var h = String(value || "").trim();
      if (h && out.indexOf(h) === -1) out.push(h);
    }
    if (!p) return out;
    add(p.handle);
    add(p.name);
    add(toHandle(p.handle));
    add(toHandle(p.name));
    add(p.sku);
    return out;
  }

  // Tries each candidate in turn until one resolves.
  function resolveProduct(candidates, label) {
    var i = 0;
    function next(lastError) {
      if (i >= candidates.length) {
        return Promise.reject(lastError || new Error("no handle"));
      }
      var handle = candidates[i++];
      return fetchProduct(handle, label).catch(next);
    }
    return next();
  }

  /**
   * Resolves the report's product handles against Shopify. The report gives a
   * handle in `name`; everything a card needs - title, price, image and the
   * variant id for Add to cart - comes from the AJAX product endpoint.
   *
   * Returns only the products that resolved, each as { api, shopify }. Any
   * that did not are reported, so a report whose cards go missing can be
   * traced back to the products behind it.
   */
  function loadProducts(products, opts) {
    var o = opts || {};
    var label = o.logLabel || "[SAT Report]";
    var failed = [];

    return Promise.all(
      (products || []).map(function (p) {
        var candidates = handleCandidates(p);
        if (!candidates.length) {
          failed.push({ product: p, error: "no handle" });
          return Promise.resolve(null);
        }
        return resolveProduct(candidates, label)
          .then(function (shopify) {
            return { api: p, shopify: shopify };
          })
          .catch(function (e) {
            console.error(label + " Could not load product", candidates, e);
            failed.push({ product: p, error: (e && e.message) || "unknown" });
            return null;
          });
      })
    ).then(function (results) {
      if (failed.length && global.SATTrack) {
        global.SATTrack.push("sat_products_unresolved", {
          source: label,
          products_failed: failed
            .map(function (f) {
              var p = f.product || {};
              return (p.name || p.handle || p.sku || "?") + " (" + f.error + ")";
            })
            .join(", "),
          failed_count: failed.length,
          product_count: (products || []).length,
        });
      }
      return results.filter(Boolean);
    });
  }

  /**
   * One recommendation card. `opts.stepTypes` drives the C-T-M-P rail: a tick
   * on the step this product covers, plain dots on the rest, all strung on one
   * connecting line. The rail always shows all four steps, whatever the
   * regimen strip above it is showing.
   */
  function productCardHTML(it, opts) {
    var o = opts || {};
    var stepTypes = o.stepTypes || [];
    var api = it.api || {};
    var v = firstAvailableVariant(it.shopify) || {};
    var pricing = pricingOf(it.shopify);
    var img = it.shopify.featured_image || (it.shopify.images && it.shopify.images[0]) || "";

    var dots = stepTypes
      .map(function (type) {
        var on = type && api.type && String(type).toLowerCase() === String(api.type).toLowerCase();
        return (
          '<span class="satr-product__ctmp-step' + (on ? " on" : "") + '">' +
            '<i class="satr-product__ctmp-dot">' + (on ? CHECK_SVG : "") + "</i>" +
            '<em class="satr-product__ctmp-letter">' + esc(String(type || "").charAt(0)) + "</em>" +
          "</span>"
        );
      })
      .join("");

    // The whole card opens the PDP; the title stays a real link so it is
    // reachable by keyboard and reads as one.
    var url = it.shopify.url || "/products/" + it.shopify.handle;
    var imgSize = o.imageSize || 200;

    // The report supplies a match score and a note; a chat recommendation
    // supplies a plain reason instead. Whichever is present shares one line.
    var note =
      (api.matchScore != null
        ? '<span class="satr-product__match">Match: <strong>' + esc(api.matchScore) + "/10</strong></span>"
        : "") +
      (it.reason ? esc(it.reason) : "") +
      (api.note ? '<strong class="satr-product__note-label">Note:</strong> ' + esc(api.note) : "");

    return (
      // handle/title are carried on the card so click and add-to-cart events
      // can name the product without scraping the rendered text.
      '<article class="satr-product' + (o.modifier ? " " + o.modifier : "") + '" data-satr-product-link="' + esc(url) + '"' +
        ' data-satr-product-handle="' + esc(it.shopify.handle || "") + '"' +
        ' data-satr-product-title="' + esc(it.shopify.title || "") + '"' +
        // Analytics and the bundle still quote the variant price; only the
        // card's own display moved to the metafield selling price.
        ' data-satr-product-price="' + esc(variantPrice(it.shopify)) + '"' +
        // The cart-update bubble repeats these rather than re-deriving them,
        // so it cannot quote a different figure than the card just did.
        ' data-satr-product-sale="' + esc(pricing.sale) + '"' +
        ' data-satr-product-compare="' + esc(pricing.compare) + '"' +
        ' data-satr-product-off="' + esc(pricing.pct) + '">' +
        (img
          ? '<img class="satr-product__img" src="' + esc(img) + '" alt="" width="' + imgSize + '" height="' + imgSize + '" loading="lazy">'
          : "") +
        (stepTypes.length ? '<div class="satr-product__ctmp">' + dots + "</div>" : "") +
        '<h3 class="satr-product__title"><a href="' + esc(url) + '">' + esc(it.shopify.title) + "</a></h3>" +
        (note ? '<div class="satr-product__note">' + note + "</div>" : "") +
        '<div class="satr-product__price">' +
          '<span class="satr-product__now">' + money(pricing.sale) + "</span>" +
          (pricing.pct > 0
            ? '<span class="satr-product__was">' + money(pricing.compare) + "</span>" +
              '<span class="satr-product__off">' + esc(pricing.pct) + "% off</span>"
            : "") +
        "</div>" +
        '<button type="button" class="satr-product__cta" data-satr-add data-variant="' + esc(v.id || "") + '">Add to cart</button>' +
      "</article>"
    );
  }

  function productsHTML(items, opts) {
    return (items || [])
      .map(function (it) {
        return productCardHTML(it, opts);
      })
      .join("");
  }

  // ------------------------------------------------------------------ steps

  /**
   * The regimen strip only shows the steps the report actually recommends, so
   * a two-product report reads as a two-step regimen. Nothing lining up leaves
   * the strip as authored rather than blanking it out.
   */
  function activateSteps(items, steps, strip) {
    var types = (items || []).map(function (it) {
      return String((it.api && it.api.type) || "").toLowerCase();
    });
    var all = Array.prototype.slice.call(steps || []);
    var matched = all.filter(function (el) {
      return types.indexOf(String(el.dataset.type || "").toLowerCase()) >= 0;
    });

    if (!matched.length) return 0;

    all.forEach(function (el) {
      var on = matched.indexOf(el) >= 0;
      el.hidden = !on;
      el.classList.toggle("satr-step--on", on);
    });

    // The connector has to stop at the outer dots, whatever the step count.
    if (strip) strip.style.setProperty("--sat-step-inset", 50 / matched.length + "%");

    return matched.length;
  }

  // ---------------------------------------------------------------- regimen

  // The order a regimen is used in. Recommendations are always shown in this
  // order, whatever order the report lists them in.
  var REGIMEN_ORDER = ["cleanse", "treat", "moisturize", "protect"];

  function regimenRank(it) {
    var rank = REGIMEN_ORDER.indexOf(String((it && it.api && it.api.type) || "").toLowerCase());
    return rank === -1 ? REGIMEN_ORDER.length : rank;
  }

  /**
   * Returns the products in C-T-M-P order. Products with an unknown type go
   * last, and products of the same type keep the order the report gave them.
   */
  function sortByRegimen(items) {
    return (items || [])
      .map(function (it, i) {
        return { it: it, i: i };
      })
      .sort(function (a, b) {
        return regimenRank(a.it) - regimenRank(b.it) || a.i - b.i;
      })
      .map(function (entry) {
        return entry.it;
      });
  }

  // ----------------------------------------------------------------- bundle

  /** The bundle is priced off the same variants its Add To Cart sends. */
  function bundleTotal(items) {
    return (items || []).reduce(function (sum, it) {
      return sum + variantPrice(it.shopify);
    }, 0);
  }

  function bundleLines(items) {
    return (items || [])
      .map(function (it) {
        var v = firstAvailableVariant(it.shopify) || {};
        return { id: v.id, quantity: 1 };
      })
      .filter(function (i) {
        return i.id;
      });
  }

  // ------------------------------------------------------------------- cart

  /**
   * Stamps SAT_LINE_PROPERTY onto each line without disturbing properties a
   * caller already set.
   *
   * The property is deliberately part of the line's identity: Shopify merges
   * lines only when the variant *and* the properties match, so a product added
   * from the report stays a separate line from the same product added on its
   * PDP. That split is the point - it is what keeps attribution per source -
   * and it is why the cart can legitimately show one product twice.
   */
  function satLines(items) {
    return (items || []).map(function (line) {
      var properties = Object.assign({}, line.properties);
      properties[SAT_LINE_PROPERTY] = "true";
      return Object.assign({}, line, { properties: properties });
    });
  }

  /**
   * Records on the cart that it was built through the SAT flow.
   *
   * /cart/update.js merges attributes rather than replacing them, so this
   * leaves the discount_code applyDiscount writes alone.
   */
  function markCartAsSAT(opts) {
    var label = (opts && opts.logLabel) || "[SAT Report]";
    var attributes = {};
    attributes[SAT_CART_ATTRIBUTE] = "true";

    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: attributes }),
    }).catch(function (e) {
      // Attribution must never cost the customer the add that just succeeded.
      console.error(label + " Could not mark cart as SAT", e);
    });
  }

  function addToCart(items, opts) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: satLines(items) }),
    })
      .then(function (res) {
        if (res.ok) return res.json();
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            throw new Error(body.description || body.message || "Could not add to cart.");
          });
      })
      .then(function (added) {
        // After the add, so a failed add never marks the cart - and awaited,
        // so a customer who goes straight to checkout still carries the flag.
        return markCartAsSAT(opts).then(function () {
          return added;
        });
      });
  }

  function setDiscountCookie(code) {
    document.cookie = "discount_code=" + encodeURIComponent(code) + "; path=/; max-age=" + 60 * 60 * 24 * 7;
  }

  /**
   * Shopify itself only applies a discount code at checkout, which reads it
   * from the discount_code cookie. How the cart gets to show it depends on
   * which cart the SAT flow uses - see cartType().
   *
   * Never rejects: the products are already in the cart by now, so a coupon
   * that does not apply must not read as a failed Add to cart.
   */
  function applyDiscount(code, opts) {
    var label = (opts && opts.logLabel) || "[SAT Report]";
    if (!code) return Promise.resolve();
    return cartType() === "custom" ? applyCouponToDrawer(code, label) : recordDiscountOnCart(code, label);
  }

  /** GoKwik's side cart picks the code up from the cart attributes. */
  function recordDiscountOnCart(code, label) {
    setDiscountCookie(code);
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { discount_code: code, discount_applied: "true" } }),
    }).catch(function (e) {
      console.error(label + " Could not record discount on cart", e);
    });
  }

  /**
   * The custom drawer validates coupons against the coupons apply API and keeps
   * the result in its own storage (assets/copy-code.js). It strips discount_*
   * cart attributes, so none are written here. The cookie is only set once the
   * API accepts the code, as the drawer's own coupon cards do - a rejected code
   * left in the cookie would fail at checkout.
   */
  function applyCouponToDrawer(code, label) {
    if (typeof global.applyCouponAPI !== "function") {
      console.error(label + " Coupon apply API missing - is the cart drawer on this page?");
      return Promise.resolve(null);
    }

    var cart = null;
    return fetch("/cart.js")
      .then(function (res) {
        if (!res.ok) throw new Error("Could not read the cart.");
        return res.json();
      })
      .then(function (fresh) {
        cart = fresh;
        return global.applyCouponAPI(code, cart);
      })
      .then(function (result) {
        var coupon =
          result && result.success && typeof global.resolveValidAppliedCoupon === "function"
            ? global.resolveValidAppliedCoupon(result, code)
            : null;
        if (!coupon) {
          console.warn(label + " Coupon " + code + " was not applied", result);
          return null;
        }

        setDiscountCookie(code);
        var updatedCart = (result.data && result.data.updatedCart) || null;
        var discountData = (updatedCart && updatedCart.discount) || coupon;
        if (typeof global.showDiscountInSubtotal === "function") {
          global.showDiscountInSubtotal(discountData, code, updatedCart, cart);
        }
        return { discountData: discountData, updatedCart: updatedCart, cart: cart };
      })
      .catch(function (e) {
        console.error(label + " Could not apply coupon " + code, e);
        return null;
      });
  }

  /**
   * Which cart the SAT flow opens - 'gokwik' (the default) or 'custom', from
   * the theme setting published by snippets/sat-cart-config.liquid.
   */
  function cartType() {
    return global.SATCartConfig && global.SATCartConfig.type === "custom" ? "custom" : "gokwik";
  }

  function openGokwikCart() {
    if (typeof global.openGokwikSideCart !== "function") return false;
    global.openGokwikSideCart();
    return true;
  }

  function openCustomCartDrawer() {
    var drawer = document.getElementById("CartDrawer") || document.querySelector("hdt-cart-drawer");
    if (!drawer) return false;
    if (typeof drawer.openDialog === "function") drawer.openDialog();
    else drawer.setAttribute("open", "");
    return true;
  }

  /**
   * Opens the configured cart. Returns false when it is not on the page, so
   * the caller picks its own fallback - there is no silent switch to the
   * other cart, which would hide a misconfiguration.
   */
  function openCart() {
    return cartType() === "custom" ? openCustomCartDrawer() : openGokwikCart();
  }

  /**
   * The custom drawer is server-rendered and only re-renders on cart:refresh,
   * so it has to be told after an AJAX add. GoKwik's side cart reads the cart
   * itself when it opens, so it is left alone.
   *
   * The event carries the live cart: without one the drawer's coupon code
   * falls back to its cached cart, and if that is the empty cart from before
   * the add it clears the coupon just applied.
   */
  function syncCartDrawer() {
    if (cartType() !== "custom") return Promise.resolve();
    return fetch("/cart.js")
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent("cart:refresh", { detail: cart ? { cart: cart } : {} }));
      });
  }

  /**
   * The label lifecycle of an Add to cart button: 'Adding…' while the request
   * is in flight, 'Added' as a confirmation, then back to the label it was
   * authored with. The pristine label is kept on the element - reading
   * textContent again mid-run would capture a transient state and make it
   * permanent.
   */
  function setAddButtonState(btn, state) {
    if (!btn) return;
    if (!btn.dataset.satrLabel) btn.dataset.satrLabel = btn.textContent.trim();
    var label = btn.dataset.satrLabel;
    clearTimeout(Number(btn.dataset.satrRevert));

    if (state === "loading") {
      btn.disabled = true;
      btn.textContent = "Adding…";
      return;
    }

    btn.disabled = false;
    if (state === "added") {
      btn.textContent = "Added";
      // 'Added' is a confirmation, not a resting state - the card goes back to
      // offering the product so it can be added again.
      btn.dataset.satrRevert = setTimeout(function () {
        btn.textContent = label;
      }, ADDED_REVERT_MS);
      return;
    }

    btn.textContent = label;
  }

  global.SATReport = {
    CHECK_SVG: CHECK_SVG,
    ADDED_REVERT_MS: ADDED_REVERT_MS,
    esc: esc,
    money: money,
    firstAvailableVariant: firstAvailableVariant,
    variantPrice: variantPrice,
    pricingOf: pricingOf,
    addedLineTotal: addedLineTotal,
    addedLinePricing: addedLinePricing,
    pricingFromCard: pricingFromCard,
    severityOf: severityOf,
    concernsHTML: concernsHTML,
    fetchProduct: fetchProduct,
    loadProducts: loadProducts,
    productCardHTML: productCardHTML,
    productsHTML: productsHTML,
    activateSteps: activateSteps,
    sortByRegimen: sortByRegimen,
    bundleTotal: bundleTotal,
    bundleLines: bundleLines,
    satLines: satLines,
    markCartAsSAT: markCartAsSAT,
    addToCart: addToCart,
    applyDiscount: applyDiscount,
    cartType: cartType,
    openCart: openCart,
    syncCartDrawer: syncCartDrawer,
    setAddButtonState: setAddButtonState,
  };
})(window);
